// @ts-nocheck
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import constants from "@store/constants";
import { getStoreListState } from "@store/Framework/Store/Data/StoreLists/StoreListsSelector";
import { linkStyles } from "@store/Themes/common/mixins/link";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        listStore: getStoreListState(state, state.store.pages.storeListsDetails.storeListId).value,
        storeListLink: getPageLinkByPageType(state, "StoreListPage"),
        language: state.context.session.language,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface StoreListsDetailsHeaderStyles {
    container?: GridContainerProps;
    infoGridItem?: GridItemProps;
    lastUpdateText?: TypographyProps;
    nameGridItem?: GridItemProps;
    nameText?: TypographyProps;
    backLinkGridItem?: GridItemProps;
    backLink?: LinkPresentationProps;
}

export const headerStyles: StoreListsDetailsHeaderStyles = {
    container: { gap: 15 },
    infoGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            align-items: flex-start;

            @media print {
                justify-content: flex-start;
                flex-direction: row !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-direction: column;
                        `,
                        null,
                        null,
                        css`
                            justify-content: flex-start;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    lastUpdateText: {
        css: css`
            padding-right: ${pxToRem(30)};
            font-style: italic;
        `,
    },

    nameGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    nameText: {
        variant: "h1",
        css: css`
            margin-bottom: ${pxToRem(5)};
        `,
    },
    backLinkGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;

            ${breakpoint("xs", "only")} {
                justify-content: flex-start;
            }
        `,
    },
    backLink: {
        typographyProps: {
            css: css`
                ${linkStyles("secondary", "secondary", false, true)};
                font-size: ${x => x.theme.design.typography.size.base};
                font-weight: ${x => x.theme.design.typography.weight.bold};
            `,
        },
        icon: {
            iconProps: {
                color: "secondary",
                size: 26,
                src: ChevronLeft,
                css: css`
                    margin-right: 0;
                `,
            },
        },
    },
};

const styles = headerStyles;

const StoreListsDetailsHeader = ({ listStore, storeListLink, language }: Props) => {
    if (!listStore) {
        return null;
    }

    const createdOnDisplay = getLocalizedDateTime({
        dateTime: new Date(listStore.createdOn),
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.backLinkGridItem}>
                <Link {...styles.backLink} href={storeListLink?.url}>
                    {translate("Return to Stores List")}
                </Link>
            </GridItem>
            <GridItem {...styles.nameGridItem}>
                <Typography {...styles.nameText} data-test-selector="listName">
                    {listStore.name}
                </Typography>
                <Typography data-test-selector="listDescription">{listStore.description}</Typography>
            </GridItem>
            <GridItem {...styles.infoGridItem}>
                <Typography {...styles.lastUpdateText} data-test-selector="lastUpdate">
                    {translate("Created On")} {createdOnDisplay}
                </Typography>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(StoreListsDetailsHeader),
    definition: {
        group: constants.widgetGroup.storeListDetails,
        displayName: "Store List Header",
    },
};

export default widgetModule;
